import { useState, useRef, useEffect } from 'react';
import store from 'store';
import uniqby from 'lodash.uniqby';

import { STORE_NAMESPACE } from '../../components/global/DismissiblePersistent';
import { getSelfieCaptureError } from '../../components/global/selfie-capture/utilities';
import { SELFIE_CAPTURE_ERROR_CODES } from '../../components/global/selfie-capture/constants';
import useRequestConfirm from '../useRequestConfirm';
import { getErrorString } from '../../util/normalize-errors';
import { isIOS } from '../../util/device-detector';
import getLogger from '../../util/logger';

const logger = getLogger('useWebRTCStream');

/**
 * Hook to handle & manage all video recording using WebRTC
 *
 * @param {Object} opts List of options
 * @param {String} opts.storeKey Store key for recording camera permissions.
 * @param {String} opts.onError Method to report errors to
 * @returns {{ onStreamInit: Function, onDeviceChange: Function, loadingDevices: Boolean, getCameraPermission: Function, cameraStream: React.Ref, cameraStreamElementRef: React.Ref }}
 */
const useWebRTCStream = ({ storeKey, onError }) => {
  const requestConfirm = useRequestConfirm();
  const [cameraPermission, setCameraPermission] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [devices, setDevices] = useState([]);
  const [currentDevice, setCurrentDevice] = useState(null);

  const cameraStream = useRef(null);
  const cameraStreamElementRef = useRef(null);

  const getDevices = async () => {
    try {
      const browserDevices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = uniqby(
        browserDevices.filter(device => device.kind === 'videoinput'),
        device => device.deviceId,
      );

      if (videoDevices.length === 0) {
        throw getSelfieCaptureError(SELFIE_CAPTURE_ERROR_CODES.SC002);
      }

      logger.info('getDevices() :: ', videoDevices);

      // Based on the devices gotten, fill in a current device
      let nextDevice = null;
      const frontCamera = videoDevices.find(device =>
        device.label.toLowerCase().includes('front'),
      );

      // Useful for Android specially, which names their cameras front & back
      if (frontCamera) {
        nextDevice = frontCamera;
      }

      // in iOS, 1st device seems to be the back camera. 2nd is Front from what we've
      // been able to test, which is why we'll assume this to be the default device.
      // --
      // As of 2023 - it seems new iOS devices do label the front camera as "Front".
      // Since that's the case, we're adding an extra check just to ensure this happens
      // only if the frontCamera is not found.
      if (isIOS() && videoDevices.length >= 2 && !frontCamera) {
        nextDevice = videoDevices[1];
      }

      if (currentDevice) {
        nextDevice = currentDevice;
      }

      if (!nextDevice) {
        [nextDevice] = videoDevices;
      }

      setDevices(videoDevices);
      setCurrentDevice(nextDevice);
    } catch (error) {
      const selfieCaptureError =
        getSelfieCaptureError(error.name) || getErrorString(error);

      onError(selfieCaptureError);
      throw error;
    }
  };

  const getVideoTrackDimensions = () => {
    if (!cameraStream.current) {
      return {};
    }

    const { width, height } = cameraStream.current
      .getVideoTracks()[0]
      .getSettings();

    return { width, height };
  };

  const onStreamInit = async () => {
    if (cameraStream.current) {
      logger.info(
        'onStreamInit :: Removing current stream',
        cameraStream.current,
      );
      cameraStream.current.getTracks().forEach(track => {
        track.stop();
      });

      cameraStream.current = null;
    }

    const constraints = {
      video: {
        // Passing undefined will make it user `facingMode` & guess a device
        // Most of these constraints go for ideal values, so it should not fail at all
        // unless no cameras are found whatsoever.
        deviceId: currentDevice?.deviceId
          ? { exact: currentDevice.deviceId }
          : undefined,
        width: { min: 320, ideal: 1980, max: 1920 },
        height: { min: 240, ideal: 1080, max: 1080 },
        facingMode: 'user',
      },

      audio: true,
    };

    setLoadingDevices(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      // we must get devices *after* requesting permissions, otherwise iOS
      // will not retrieve the full set of available devices.
      if (!devices.length) {
        await getDevices();
      }

      cameraStream.current = stream;

      logger.info(
        'onStreamInit :: Camera Stream Element: ',
        cameraStreamElementRef.current,
      );

      if (cameraStreamElementRef.current) {
        cameraStreamElementRef.current.srcObject = stream;
      }

      const { width, height } = getVideoTrackDimensions();

      logger.info(`onStreamInit :: Video Track Resolution: `, {
        width,
        height,
      });
    } catch (error) {
      const selfieCaptureError =
        getSelfieCaptureError(error.name) || getErrorString(error);
      onError(selfieCaptureError);
    } finally {
      setLoadingDevices(false);
    }
  };

  const getCameraPermission = () => {
    const localStore = store.get(`${STORE_NAMESPACE}${storeKey}`) || {};
    const { cameraPermissionGranted = [] } = localStore;

    const hasGrantedPermission = storeKey
      ? cameraPermissionGranted.includes(storeKey)
      : false;

    if (cameraPermission || hasGrantedPermission) {
      setCameraPermission(true);
      onStreamInit();
      return;
    }

    requestConfirm({
      message:
        'Please grant access to camera below so I can record your video.',
      confirmText: 'Yes',
      cancelText: 'No',
      onCancel: () => {
        onError(getSelfieCaptureError(SELFIE_CAPTURE_ERROR_CODES.SC001));
      },
      onConfirm: async () => {
        setCameraPermission(true);
        if (storeKey) {
          store.set(`${STORE_NAMESPACE}${storeKey}`, {
            ...localStore,
            cameraPermissionGranted: [...cameraPermissionGranted, storeKey],
          });
        }

        onStreamInit();
      },
    });
  };

  const onDeviceChange = () => {
    const currentDeviceIndex = devices
      .map(device => device.deviceId)
      .indexOf(currentDevice.deviceId);

    const nextDevice = devices[currentDeviceIndex + 1] || devices[0];
    setCurrentDevice(nextDevice);
  };

  useEffect(() => {
    if (currentDevice) {
      onStreamInit();
    }
  }, [currentDevice]);

  return {
    onStreamInit,
    onDeviceChange,
    loadingDevices,
    getCameraPermission,
    cameraPermission,
    cameraStream,
    cameraStreamElementRef,
    devices,
  };
};

export default useWebRTCStream;
